/** Normalize smart quotes so substring matchers stay reliable across browsers. */
export function normalizeErrorMessageText(message: string): string {
  return message.replace(/[\u2018\u2019]/g, "'").replace(/[\u201c\u201d]/g, '"')
}

export function formatNamedErrorMessage(name: string, message: string): string {
  const msg = normalizeErrorMessageText(message)
  return name && name !== 'Error' ? `${name}: ${msg}` : msg
}

/**
 * Normalize an unknown rejection or catch value to a single-line message string.
 * Handles string, `Error` (including DOMException in browsers), plain objects with optional
 * `name` / `message`, chained `cause` / `reason`, and a depth limit so cycles cannot recurse
 * forever.
 */
export function flattenCaughtMessage(reason: unknown, depth = 0): string {
  if (depth > 4 || reason == null) {
    return ''
  }
  if (typeof reason === 'string') {
    return normalizeErrorMessageText(reason)
  }
  if (reason instanceof Error) {
    const base = formatNamedErrorMessage(reason.name, reason.message)
    const errCause = (reason as Error & { cause?: unknown }).cause
    if (errCause != null && errCause !== reason) {
      const nested = flattenCaughtMessage(errCause, depth + 1)
      return nested ? `${base} ${nested}` : base
    }
    return base
  }
  if (typeof reason === 'object') {
    const rec = reason as Record<string, unknown>
    const name = typeof rec.name === 'string' ? rec.name : ''
    const msg = rec.message
    const chained = rec.cause ?? rec.reason

    if (typeof msg === 'string') {
      const base = formatNamedErrorMessage(name, msg)
      if (chained != null && chained !== reason) {
        const nested = flattenCaughtMessage(chained, depth + 1)
        return nested ? `${base} ${nested}` : base
      }
      return base
    }
    if (chained != null && chained !== reason) {
      return flattenCaughtMessage(chained, depth + 1)
    }
  }
  return ''
}

export const getErrorCode = (error: unknown): string | undefined => {
  if (error && typeof error === 'object' && 'code' in error) {
    return String((error as { code: unknown }).code)
  }
  return undefined
}

export const getErrorMessage = (error: unknown): string | undefined => {
  const flat = flattenCaughtMessage(error)
  if (flat !== '') {
    return flat
  }
  // Legacy: some payloads expose non-string `message` (coerce like the previous implementation).
  if (error && typeof error === 'object' && 'message' in error) {
    return normalizeErrorMessageText(String((error as { message: unknown }).message))
  }
  return undefined
}

/**
 * Firebase callable rejected the call because the account has no active subscription
 * (e.g. `zoom_generateToken`): `functions/permission-denied` and/or 403 with this message body.
 */
export function isCallableActiveSubscriptionRequiredError(error: unknown): boolean {
  const code = getErrorCode(error)
  const message = getErrorMessage(error) ?? ''
  if (!message.includes('Active subscription required')) {
    return false
  }
  return code === 'functions/permission-denied' || /\b403\b/.test(message)
}
